import "./styles/main.scss";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { InitialRouteProvider } from "./context/InitialRouteContext";
import Header from "./components/Header";
// import Footer from "./components/Footer"; // Unused for now
import { ThemeProvider } from "./context/ThemeContext";
import HeadingAnimator from "./components/HeadingAnimator";
import CookieBanner from "./components/CookieBanner";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import DocumentTitleTabCycle from "./components/DocumentTitleTabCycle";
import SvgPathDrawOnLoad from "./components/SvgPathDrawOnLoad";
import LoaderBootstrap from "./components/LoaderBootstrap";
import { registerLoaderEndDebug } from "./utils/loaderEndDebug";
import {
  bindHeroBubbleAudioUnlock,
  preloadHeroBubblePopSounds,
} from "./utils/heroBubbleAudio";
import { isFigmaQaPath } from "./utils/figmaQaRoute";
import { isHomePath } from "./utils/homePath";
import i18n from "./i18n";

registerLoaderEndDebug();

// Lazy load routes - only Home is loaded immediately
const Home = lazy(() => import("./pages/Home"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const BrandingLogoArticle = lazy(() => import("./pages/BrandingLogoArticle"));
const RedesignSelektywnyOffer = lazy(() => import("./pages/RedesignSelektywnyOffer"));
const Bootstones = lazy(() => import("./pages/Bootstones"));
const Buttons = lazy(() => import("./pages/Buttons"));
const FigmaQa = lazy(() => import("./pages/FigmaQa"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Language route wrapper - syncs URL path with i18n language
const LanguageRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isQaRoute = isFigmaQaPath(location.pathname);
  const pathLang = location.pathname.startsWith('/en') ? 'en' : 'pl';
  const previousPathRef = React.useRef(location.pathname);
  const hasCheckedInitialRedirect = React.useRef(false);
  
  useEffect(() => {
    if (isQaRoute) {
      previousPathRef.current = location.pathname;
      return;
    }

    // Redirect root path to /en if no language preference is saved and default is English
    // Only check once on initial mount
    if (!hasCheckedInitialRedirect.current && location.pathname === '/') {
      hasCheckedInitialRedirect.current = true;
      try {
        const savedLang = localStorage.getItem("i18nextLng");
        if (!savedLang) {
          // No saved language preference, stay on root (default Polish)
          return;
        } else if (savedLang === 'en') {
          // User has English preference, redirect to /en
          navigate('/en', { replace: true });
          return;
        } else {
          // User has Polish preference, stay on root (Polish routes)
          return;
        }
      } catch {
        // If localStorage fails, stay on root (default Polish)
        return;
      }
    }
    
    // Check if i18n language doesn't match the path - this indicates an intentional language switch
    const i18nLang = i18n.language;
    const pathLangFromPath = location.pathname.startsWith('/en') ? 'en' : 'pl';
    
    // If i18n language doesn't match path, prioritize i18n language (user clicked language switcher)
    if (i18nLang !== pathLangFromPath) {
      // User intentionally switched language, update path to match
      const wasOnEnglish = previousPathRef.current.startsWith('/en');
      const currentPath = location.pathname;
      const basePath = wasOnEnglish ? currentPath.replace('/en', '') || '/' : currentPath;
      
      if (i18nLang === 'en') {
        // Switching to English - add /en prefix
        const newPath = `/en${basePath === '/' ? '' : basePath}${location.search}${location.hash}`;
        if (newPath !== currentPath) {
          previousPathRef.current = newPath;
          navigate(newPath, { replace: true });
          return;
        }
      } else {
        // Switching to Polish - remove /en prefix
        if (currentPath.startsWith('/en')) {
          const newPath = `${basePath}${location.search}${location.hash}`;
          previousPathRef.current = newPath;
          navigate(newPath, { replace: true });
          return;
        }
      }
    }
    
    // If navigating from /en to a path without /en (and not an intentional language switch), preserve the /en prefix
    const wasOnEnglish = previousPathRef.current.startsWith('/en');
    const isNowOnEnglish = location.pathname.startsWith('/en');
    
    if (wasOnEnglish && !isNowOnEnglish && location.pathname !== '/' && i18nLang === 'en') {
      // User was on English version and still wants English, redirect to English version of this path
      const newPath = `/en${location.pathname}${location.search}${location.hash}`;
      previousPathRef.current = newPath;
      navigate(newPath, { replace: true });
      return;
    }
    
    // Update previous path reference
    previousPathRef.current = location.pathname;

    // If language matches path, nothing else to do
    if (i18n.language === pathLang) {
      return;
    }

    // If path is Polish and i18n is Polish, nothing else to do
    if (pathLang === 'pl' && i18n.language === 'pl') {
      return;
    }

    // If switching to English, ensure translations are loaded first
    if (pathLang === 'en') {
      if (!i18n.hasResourceBundle('en', 'translation')) {
        import("./locales/en.json")
          .then((en) => {
            i18n.addResourceBundle('en', 'translation', en.default || en);
            return i18n.changeLanguage('en');
          })
          .then(() => {
            try {
              localStorage.setItem("i18nextLng", 'en');
            } catch {}
          })
          .catch(() => {
            console.warn("Failed to load English translations");
            i18n.changeLanguage('pl');
          });
      } else {
        i18n.changeLanguage('en').then(() => {
          try {
            localStorage.setItem("i18nextLng", 'en');
          } catch {}
        });
      }
    } else {
      i18n.changeLanguage('pl').then(() => {
        try {
          localStorage.setItem("i18nextLng", 'pl');
        } catch {}
      });
    }
  }, [isQaRoute, location.pathname, location.search, location.hash, pathLang, navigate]);

  return <>{children}</>;
};

// Lazy load Swiper CSS only when needed (will be imported in components that use it)
// Removed global Swiper CSS imports - will be loaded on-demand
 
// Removed glitch-related components
// import CareGlitchLoop from "./components/CareGlitchLoop";
// import AssignGlitchHalf from "./components/AssignGlitchHalf";
// import TargetedGlitch from "./components/TargetedGlitch";
// import SvgGlitchLoop from "./components/SvgGlitchLoop";
// import BannerifyHeadings from "./components/BannerifyHeadings";

// Removed Reset/Matrix effects

const AppLayout = () => {
  const location = useLocation();
  const isQaRoute = isFigmaQaPath(location.pathname);

  useEffect(() => {
    if (isQaRoute || !isHomePath(location.pathname)) return undefined;

    preloadHeroBubblePopSounds();
    return bindHeroBubbleAudioUnlock();
  }, [isQaRoute, location.pathname]);

  return (
    <div className="app">
      {!isQaRoute && <LoaderBootstrap />}
      {!isQaRoute && <DocumentTitleTabCycle />}
      {!isQaRoute && <SvgPathDrawOnLoad />}
      {!isQaRoute && <Header />}
      <Suspense fallback={<div className="route-loading-fallback" style={{ minHeight: "60vh" }} aria-hidden="true" />}>
        <Routes>
          {/* Polish routes */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/oferta/redesign-selektywny" element={<RedesignSelektywnyOffer />} />
          <Route
            path="/wiedza"
            element={<Navigate to="/wiedza/branding/logo-ktore-dba-o-twoj-wizerunek" replace />}
          />
          <Route path="/wiedza/branding/logo-ktore-dba-o-twoj-wizerunek" element={<BrandingLogoArticle />} />
          <Route path="/ui-kit" element={<Bootstones />} />
          <Route path="/bootstones" element={<Bootstones />} />
          <Route path="/buttons" element={<Buttons />} />
          <Route path="/figma-qa" element={<FigmaQa />} />
          <Route path="/porownanie" element={<FigmaQa />} />

          {/* English routes */}
          <Route path="/en" element={<Home />} />
          <Route path="/en/home" element={<Home />} />
          <Route path="/en/cookie-policy" element={<CookiePolicy />} />
          <Route
            path="/en/wiedza"
            element={<Navigate to="/en/wiedza/branding/logo-ktore-dba-o-twoj-wizerunek" replace />}
          />
          <Route path="/en/wiedza/branding/logo-ktore-dba-o-twoj-wizerunek" element={<BrandingLogoArticle />} />
          <Route path="/en/ui-kit" element={<Bootstones />} />
          <Route path="/en/bootstones" element={<Bootstones />} />
          <Route path="/en/buttons" element={<Buttons />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!isQaRoute && <HeadingAnimator />}
      {!isQaRoute && <CookieBanner />}
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <ThemeProvider>
      <CookieConsentProvider>
        <Router>
          <InitialRouteProvider>
            <LanguageRoute>
              <AppLayout />
            </LanguageRoute>
          </InitialRouteProvider>
        </Router>
      </CookieConsentProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
